import React from 'react';
import PropTypes from 'prop-types';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Edit as EditIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  DescriptionOutlined as DocxIcon,
  PictureAsPdfOutlined as PdfIcon
} from '@mui/icons-material';

/**
 * Context menu for task tiles
 * @param {Object} props - Component props
 * @param {HTMLElement} props.anchorEl - Element to anchor the menu to
 * @param {Object} props.selectedTile - Selected tile data
 * @param {Function} props.onClose - Function to close the menu
 * @param {Function} props.onEdit - Function to edit the task
 * @param {Function} props.onDownloadPDF - Function to download as PDF
 * @param {Function} props.onDownloadDOCX - Function to download as DOCX
 * @param {Function} props.onMarkAsDone - Function to mark as done
 * @param {Function} props.onRemove - Function to remove the task
 * @param {Function} props.showNotification - Function to show notifications
 * @returns {React.ReactElement} Task menu component
 */
const TaskMenu = ({
  anchorEl,
  selectedTile,
  onClose,
  onEdit,
  onDownloadPDF,
  onDownloadDOCX,
  onMarkAsDone,
  onRemove,
  showNotification
}) => {

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'right',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      elevation={3}
      sx={{
        '& .MuiPaper-root': {
          minWidth: '180px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)'
        }
      }}
    >
      <MenuItem onClick={() => {
        onEdit(selectedTile);
        onClose();
      }}>
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>

      <MenuItem onClick={() => {
        onDownloadPDF(selectedTile);
        onClose();
      }}>
        <ListItemIcon>
          <PdfIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Download PDF</ListItemText>
      </MenuItem>

      <MenuItem onClick={() => {
        onDownloadDOCX(selectedTile);
        onClose();
      }}>
        <ListItemIcon>
          <DocxIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Download docx</ListItemText>
      </MenuItem>

      <Divider />

      <MenuItem onClick={() => {
        onMarkAsDone(selectedTile);
        onClose();
      }}>
        <ListItemIcon>
          <CheckCircleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>
          {selectedTile?.status === 'completed' ? 'Mark as incomplete' : 'Mark as done'}
        </ListItemText>
      </MenuItem>

      <MenuItem onClick={() => {
        if (selectedTile?.id === 'chat-prd' || selectedTile?.id === 'extract-prd') {
          showNotification({
            message: `Cannot remove core topic: ${selectedTile?.title}`,
            type: 'warning'
          });
        } else {
          onRemove(selectedTile);
        }
        onClose();
      }}>
        <ListItemIcon>
          <DeleteIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Remove topic</ListItemText>
      </MenuItem>
    </Menu>
  );
};

TaskMenu.propTypes = {
  anchorEl: PropTypes.object,
  selectedTile: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDownloadPDF: PropTypes.func.isRequired,
  onDownloadDOCX: PropTypes.func.isRequired,
  onMarkAsDone: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired
};

export default React.memo(TaskMenu);
